// frontend/src/ui/UIComponents.jsx
import React, { useEffect } from "react";

/** --------------------
 * Global SelectInput
 * -------------------- */
export function SelectInput({
  value,
  onChange,
  choices = [],
  disabled = false,
  className = "text-input",
  style,
}) {
  return (
    <select
      className={className}
      value={value}
      onChange={onChange}
      disabled={disabled}
      style={style}
    >
      {choices.map((c, i) => (
        <option key={i} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

/** --------------------
 * Global Button (.btn)
 * -------------------- */
export function Button({ children, className = "btn", ...props }) {
  return (
    <button className={className} {...props}>
      {children}
    </button>
  );
}

/** --------------------
 * Global Modal
 *
 * Behaviors:
 * - Backdrop click closes
 * - ESC key closes
 * - Scroll-safe on mobile
 * - Sticky footer support
 *
 * Props:
 * - open        : boolean
 * - title       : string
 * - onClose     : () => void
 * - maxWidth    : number (px) cap for desktop
 * - children    : main scrollable body content
 * - footer      : optional ReactNode for bottom action buttons
 * - headerExtra : optional ReactNode for header actions (e.g. Help button)
 * -------------------- */
export function Modal({
  open,
  title = "Thông báo / Notice",
  onClose = () => {},
  maxWidth = 720,
  children,
  footer,
  headerExtra, // <--- NEW PROP ADDED HERE
}) {
  // ESC key support
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="modal-backdrop"
      onClick={(e) => {
        // Close only if click is on backdrop, not inside panel.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="modal-panel"
        style={{
          maxWidth,
        }}
      >
        <div className="modal-header">
          <h3 style={{ marginRight: "auto" }}>{title}</h3>
          
          {/* Render extra header content (HDSD button) if present */}
          {headerExtra && <div style={{ marginRight: 8 }}>{headerExtra}</div>}

          <Button className="btn modal-close-btn" onClick={onClose}>
            Đóng / Close
          </Button>
        </div>

        <div className="modal-body">{children}</div>

        {footer ? <div className="modal-footer">{footer}</div> : null}
      </div>
    </div>
  );
}

/** --------------------
 * Global TextInput
 * -------------------- */
export function TextInput({
  label,
  value,
  onChange,
  type = "text",
  min,
  helper,
  disabled,
}) {
  return (
    <label style={{ display: "block" }}>
      {label}
      <input
        type={type}
        value={value}
        onChange={onChange}
        min={min}
        disabled={disabled}
        className="text-input"
      />
      {helper && <div style={{ fontSize: 12, opacity: 0.7 }}>{helper}</div>}
    </label>
  );
}

/** --------------------
 * Global RadioGroup
 * -------------------- */
export function RadioGroup({ legend, name, options, value, onChange }) {
  return (
    <fieldset style={{ border: 0, padding: 0, marginTop: 8 }}>
      <legend style={{ fontWeight: 600, marginBottom: 6 }}>{legend}</legend>
      {options.map((opt, i) => (
        <label key={i} style={{ marginRight: 16 }}>
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
          />{" "}
          {opt.label}
        </label>
      ))}
    </fieldset>
  );
}

/** --------------------
 * Global ListBox
 * -------------------- */
export function ListBox({
  multiple = true,
  size = 8,
  value,
  onChange,
  disabled,
  children,
}) {
  return (
    <select
      multiple={multiple}
      size={size}
      style={{ width: "100%" }}
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {children}
    </select>
  );
}